import {useState} from 'react';
import {
    Alert,
    Button,
    Group,
    List,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import {AiOutlineInfoCircle, AiOutlineWarning} from 'react-icons/ai';
import {PlatformDrawer} from '@flexinet/ui-components';
import {useQuery} from '@tanstack/react-query';

import * as PlaybooksService from '../../playbooks/services/PlaybooksService';
import {Playbook, PlaybookStatus} from '../../playbooks/types';
import {Application} from '../types';
import {slugify, useNameAvailability} from '../../devops/name-availability';
import {DerivedNamesPanel} from '../components/DerivedNamesPanel';

export type ProvisionSubmitValues = {
    playbookId: string,
    stage: string,
    subdomain: string,
    baseName: string,
    nameOverrides: Record<string, string>,
};

type ProvisionFormDrawerProps = {
    opened: boolean,
    onClose: () => void,
    onConfirm: (values: ProvisionSubmitValues) => Promise<void> | void,
    provisioning?: boolean,
    record?: Application,
};

const STAGE_OPTIONS = [
    {value: 'dev', label: 'dev'},
    {value: 'test', label: 'test'},
    {value: 'prod', label: 'prod'},
];

// FNP-1500: provisioning, as a drawer, showing what it will build.
//
// ⚠️ THIS REPLACES A RAW MANTINE `Modal`. The compliance checklist mandates the
// chain AddRecordButton → FormDrawer → PlatformDrawer → RecordForm, and
// `FormDrawer` must render `PlatformDrawer`, never a raw Mantine `Drawer`.
// `ProvisionApplicationModal` was outside that chain entirely.
//
// ⚠️ THE FOUR STEPS ARE NAMED because the operator should know what they are
// agreeing to. Provisioning is two repositories and two pipelines, and each
// repository is SEEDED BEFORE its pipeline is created — that ordering is what
// makes the repository analyser honest. By the time a pipeline is provisioned
// there is real content to read, so `cdk.json` yields CDK_INFRASTRUCTURE and a
// frontend `package.json` yields STATIC_SITE. An archetype guessed from an
// empty repo would be a guess about a template the platform itself just wrote.
export const ProvisionFormDrawer = ({
                                        opened,
                                        onClose,
                                        onConfirm,
                                        provisioning = false,
                                        record,
                                    }: ProvisionFormDrawerProps) => {

    const [playbookId, setPlaybookId] = useState<string | null>(null);
    const [stage, setStage] = useState<string | null>(null);
    const [subdomain, setSubdomain] = useState<string | null>(null);
    const [baseName, setBaseName] = useState<string | null>(null);
    const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});

    // ⚠️ SEEDED THROUGH `slugify`, NOT RAW. Since FNP-1498 the record's name is
    // a human-friendly display name — "Admin Console Localhost" — and pasting
    // that straight in produced a base name the rules reject on sight, so the
    // drawer opened already showing an error the operator did not cause. The
    // suggestion has to be a name they could actually provision.
    //
    // Still editable: a slug is a guess at intent, not a decision.
    const seededBaseName = baseName ?? slugify(record?.name) ?? '';

    // FNP-1231: an EMPTY subdomain claims the zone apex. That is a real
    // feature — flexinet-marketing-app deploys at the apex deliberately — but
    // an apex is almost always already owned, so an unintended one asks
    // CloudFront for a CNAME another distribution holds and is refused with a
    // 409, after both repos and both pipelines exist.
    //
    // ⚠️ AND IT IS A SINGLE DNS LABEL, so it is slugified for the same reason
    // and then stripped of dots — "Admin Console Localhost" is not a hostname.
    // `record.subdomain` is already a label and is taken as-is.
    const seededSubdomain = subdomain
        ?? record?.subdomain
        ?? slugify(record?.name)?.replace(/\./g, '-')
        ?? '';

    // ⚠️ NO `targetStages` YET, AND THAT IS A KNOWN LIMITATION, NOT AN
    // OVERSIGHT. A TargetStage carries {stage, accountId}, and the account is
    // exactly what FNP-1518 shows is still hardcoded in the prod playbook —
    // `pipelineDeploymentAccounts` is pinned to shared-services prod. Passing a
    // guessed account would check the stack and bucket names in the WRONG
    // account and report them free when they are not.
    //
    // Omitting it checks the account-INDEPENDENT names — both repositories and
    // both pipelines — which is where the collision the owner hit actually
    // lives. The per-stage stack and bucket join once FNP-1518 makes the
    // deployment account a real choice.
    const {
        result: availability,
        isFetching: checkingNames,
        isPending: namesPending,
        allFree,
    } = useNameAvailability(seededBaseName, {
        enabled: opened && Boolean(stage),
    });

    const {data, isLoading, isError} = useQuery({
        queryKey: ['playbooks', 'provision-options'],
        queryFn: () => PlaybooksService.fetchRecords({}),
        enabled: opened,
        staleTime: 5 * 60 * 1000,
    });

    const playbooks: Playbook[] = data?.items ?? [];

    // Only an ACTIVE playbook bound to a state machine can run. workflows-api
    // enforces both — this filter spares the operator a choice that would be
    // refused, and is NOT a second gate. Do not add validation here.
    const runnable = playbooks.filter(playbook =>
        playbook.status === PlaybookStatus.ACTIVE && !!playbook.stateMachineArn,
    );

    const options = runnable.map(playbook => ({
        value: playbook.id,
        label: playbook.displayName?.trim() || playbook.name?.trim() || playbook.id,
    }));

    const skipped = playbooks.length - runnable.length;

    // ⚠️ A COLLISION OR AN UNCHECKABLE NAME BLOCKS PROVISION. `allFree` is
    // false for an UNKNOWN exactly as for a TAKEN, because a listing that
    // failed permissions returns zero items and proves nothing.
    const namesReady = allFree && !namesPending;
    const ready = Boolean(playbookId && stage) && namesReady && !provisioning;

    const handleOverride = (kind: string, value: string) => {
        setNameOverrides((current) => ({...current, [kind]: value}));
    };

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={'Provision infrastructure'}
            size={'xl'}
        >
            <Stack gap={'md'}>

                <Alert variant={'light'} color={'blue'} icon={<AiOutlineInfoCircle/>}>
                    <Text size={'sm'} fw={500} mb={'xs'}>This runs four steps</Text>
                    <List size={'sm'} type={'ordered'}>
                        <List.Item>Create the infrastructure repository and seed it</List.Item>
                        <List.Item>Create its pipeline</List.Item>
                        <List.Item>Create the application repository and seed it</List.Item>
                        <List.Item>Create its pipeline</List.Item>
                    </List>
                    <Text size={'xs'} c={'dimmed'} mt={'xs'}>
                        Each repository is seeded before its pipeline is built,
                        so the pipeline is chosen from what the repository
                        actually contains. It takes several minutes.
                    </Text>
                </Alert>

                {isError && (
                    <Alert variant={'light'} color={'red'} icon={<AiOutlineInfoCircle/>}>
                        The playbooks could not be read, so there is nothing to
                        choose from.
                    </Alert>
                )}

                <Select
                    label={'Playbook'}
                    description={'The workflow that will build it. Only ACTIVE playbooks bound to a state machine can run.'}
                    placeholder={isLoading ? 'Loading…' : 'Select a playbook'}
                    data={options}
                    value={playbookId}
                    onChange={setPlaybookId}
                    disabled={isLoading || provisioning}
                    withAsterisk
                    searchable
                />

                {skipped > 0 && (
                    <Text size={'xs'} c={'dimmed'}>
                        {skipped} playbook{skipped === 1 ? ' is' : 's are'} not
                        listed — a playbook must be ACTIVE and bound to a state
                        machine before it can run.
                    </Text>
                )}

                <Select
                    label={'Stage'}
                    description={'Which environment to provision into. Chosen deliberately — there is no default.'}
                    placeholder={'Select a stage'}
                    data={STAGE_OPTIONS}
                    value={stage}
                    onChange={setStage}
                    disabled={provisioning}
                    withAsterisk
                />

                {/*
                  * ⚠️ THE BASE NAME IS NOT THE APPLICATION NAME. Since FNP-1498
                  * the record's name is human-friendly; this is the stem every
                  * repository, pipeline, stack and bucket name is derived from.
                  * Keeping them separate is what lets an application be called
                  * "Flexi Testing" while its repos are `flexi-testing-*`.
                  */}
                <TextInput
                    label={'Resource base name'}
                    description={'Repositories, pipelines, stacks and the deployment bucket are named from this. At most 41 characters.'}
                    value={seededBaseName}
                    onChange={(e) => setBaseName(e.currentTarget.value)}
                    disabled={provisioning}
                    withAsterisk
                    data-testid={'provision-base-name'}
                />

                {stage ? (
                    <DerivedNamesPanel
                        result={availability}
                        isFetching={checkingNames || namesPending}
                        overrides={nameOverrides}
                        onOverride={handleOverride}
                    />
                ) : (
                    <Text size={'xs'} c={'dimmed'}>
                        Choose a stage to see what will be created.
                    </Text>
                )}

                <TextInput
                    label={'Subdomain'}
                    description={'The leftmost DNS label, e.g. "reports" for reports.testing.flexi.network.'}
                    placeholder={'Leave empty to deploy at the domain apex'}
                    value={seededSubdomain}
                    onChange={(e) => setSubdomain(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-subdomain'}
                />

                {seededSubdomain.trim() === '' && (
                    <Alert
                        variant={'light'}
                        color={'orange'}
                        icon={<AiOutlineWarning/>}
                        title={'This will deploy at the domain apex'}
                    >
                        <Text size={'sm'}>
                            With no subdomain this application claims the parent
                            domain itself. If another application already serves
                            that domain, provisioning fails partway — after the
                            repositories and pipelines have been created — and
                            the stack rolls back.
                        </Text>
                    </Alert>
                )}

                <Group justify={'flex-end'} mt={'md'}>
                    <Button variant={'subtle'} onClick={onClose} disabled={provisioning}>
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!playbookId || !stage) {
                                return;
                            }
                            // ⚠️ AWAIT BEFORE CLOSING. The reference drawer
                            // calls handleSubmit and closes in the same tick,
                            // which the checklist names at §7: a server-side
                            // refusal destroys the operator's input and toasts
                            // into a drawer that is no longer on screen. Here
                            // a refusal leaves the drawer open with everything
                            // still typed.
                            await onConfirm({
                                playbookId,
                                stage,
                                subdomain: seededSubdomain.trim(),
                                baseName: seededBaseName.trim(),
                                nameOverrides,
                            });
                            onClose();
                        }}
                        disabled={!ready}
                        loading={provisioning}
                    >
                        Provision
                    </Button>
                </Group>

            </Stack>
        </PlatformDrawer>
    );
};
